import type { ReactNode } from 'react';
import { splitLines } from '../roleForm';
import { EMPLOYER_CLASSIFICATION_WARNING, type JobRoleContent } from '../types';

export interface JobDescriptionHeader {
  title: string;
  roleCode?: string | null | undefined;
  departmentName?: string | null | undefined;
  jobFamily?: string | null | undefined;
  careerLevel?: string | null | undefined;
  managementLevel?: string | null | undefined;
  socCode?: string | null | undefined;
  onetCode?: string | null | undefined;
}

const NOT_PROVIDED = 'Not provided';

export function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="jr-block">
      <h4>{title}</h4>
      {children}
    </section>
  );
}

export function TextBlock({ title, text }: { title: string; text: string | null | undefined }) {
  return (
    <Block title={title}>
      {text?.trim() ? <p>{text}</p> : <p className="muted">{NOT_PROVIDED}</p>}
    </Block>
  );
}

export function ListBlock({ title, items }: { title: string; items: string[] }) {
  return (
    <Block title={title}>
      {items.length > 0 ? <ul>{items.map((item, i) => <li key={`${i}-${item}`}>{item}</li>)}</ul> : <p className="muted">{NOT_PROVIDED}</p>}
    </Block>
  );
}

export function Facts({ items }: { items: [string, ReactNode][] }) {
  return (
    <dl className="jr-facts">
      {items.map(([label, value]) => (
        <div key={label} className="jr-facts__row">
          <dt>{label}</dt>
          <dd>{value === null || value === undefined || value === '' ? <span className="muted">{'—'}</span> : value}</dd>
        </div>
      ))}
    </dl>
  );
}

const yesNo = (value: boolean | null | undefined) => (value === true ? 'Yes' : value === false ? 'No' : null);

/**
 * The employer's own classification guidance. The FLSA and overtime fields are the EMPLOYER's classification, never an automatic
 * legal determination, so the review warning is always shown right beside them. Typical pay type is only how the role is usually
 * paid: there is no rate anywhere in a role.
 */
export function EmploymentGuidance({ content }: { content: JobRoleContent }) {
  const g = content.employmentGuidance;
  return (
    <Block title="Employment guidance">
      <Facts
        items={[
          ['Typical employment type', g.typicalEmploymentType],
          ['Typical pay type', g.typicalPayType],
          ['Hourly or salaried', g.hourlyOrSalaried],
          ['Commission eligible', yesNo(g.commissionEligible)],
          ['Bonus eligible', yesNo(g.bonusEligible)],
          ['Benefit eligibility guidance', g.benefitEligibilityGuidance],
        ]}
      />
      <h5 className="jr-classification-title">Employer classification</h5>
      <p className="jr-warning" role="note">
        <span aria-hidden="true">{'⚠'} </span>
        {EMPLOYER_CLASSIFICATION_WARNING}
      </p>
      <Facts
        items={[
          ['FLSA classification (employer-selected)', g.flsaClassification],
          ['Overtime eligible (employer classification)', yesNo(g.overtimeEligible)],
        ]}
      />
    </Block>
  );
}

export function ResponsibilitiesPanel({ content, hidePurpose = false }: { content: JobRoleContent; hidePurpose?: boolean }) {
  return (
    <div className="jr-description">
      {!hidePurpose && <TextBlock title="Purpose" text={content.purpose} />}
      <ListBlock title="Essential duties" items={content.essentialDuties} />
      <ListBlock title="Responsibilities" items={content.responsibilities} />
      <ListBlock title="Expected outcomes" items={content.expectedOutcomes} />
      <TextBlock title="Decision authority" text={content.decisionAuthority} />
      <TextBlock title="Budget authority" text={content.budgetAuthority} />
      <TextBlock title="Supervisory responsibilities" text={content.supervisoryResponsibilities} />
      <TextBlock title="Internal relationships" text={content.internalRelationships} />
      <TextBlock title="External relationships" text={content.externalRelationships} />
      <ListBlock title="Compliance responsibilities" items={content.complianceResponsibilities} />
      <ListBlock title="Performance indicators" items={content.performanceIndicators} />
      <TextBlock title="Reports to" text={content.reportsTo} />
    </div>
  );
}

export function QualificationsPanel({ content }: { content: JobRoleContent }) {
  const q = content.qualifications;
  return (
    <div className="jr-description">
      <TextBlock title="Minimum education" text={q.minimumEducation} />
      <TextBlock title="Preferred education" text={q.preferredEducation} />
      <TextBlock title="Minimum experience" text={q.minimumExperience} />
      <TextBlock title="Preferred experience" text={q.preferredExperience} />
      <ListBlock title="Licenses" items={q.licenses} />
      <ListBlock title="Certifications" items={q.certifications} />
      <ListBlock title="Technical skills" items={q.technicalSkills} />
      <ListBlock title="Professional skills" items={q.professionalSkills} />
      <ListBlock title="Preferred skills" items={q.preferredSkills} />
      <ListBlock title="Languages" items={q.languages} />
      <TextBlock title="Travel" text={q.travel} />
      <TextBlock title="Work arrangement" text={q.workArrangement} />
      <ListBlock title="Physical requirements" items={splitLines(q.physicalRequirements ?? '')} />
      <ListBlock title="Working conditions" items={splitLines(q.workingConditions ?? '')} />
    </div>
  );
}

/** The whole job description in reading order: what a person reviews before activating a role, and what a past version shows. */
export function JobDescription({ header, content }: { header: JobDescriptionHeader; content: JobRoleContent }) {
  return (
    <article className="jr-jd" aria-label={`Job description for ${header.title || 'this role'}`}>
      <h3>{header.title || 'Untitled role'}</h3>
      <Facts
        items={[
          ['Internal code', header.roleCode],
          ['Department', header.departmentName],
          ['Job family', header.jobFamily],
          ['Level', header.careerLevel],
          ['Management level', header.managementLevel],
          ['SOC code', header.socCode],
          ['O*NET code', header.onetCode],
        ]}
      />
      <TextBlock title="Summary" text={content.summary} />
      <ListBlock title="Alternate titles" items={content.alternateTitles} />
      <ResponsibilitiesPanel content={content} />
      <h4 className="jr-jd__part">Qualifications</h4>
      <QualificationsPanel content={content} />
      <EmploymentGuidance content={content} />
    </article>
  );
}
